import React, { useEffect, useState } from 'react'
import { IonButton, IonButtons, IonCol, IonGrid, IonItemDivider, IonLabel, IonList, IonRow } from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import { getPatientDemographicsSelector } from '../selectors'
import {
  savePatientDemographics,
  setAddress1,
  setAddress2,
  setAddressPNNV,
  setCity,
  setCityPNNV,
  setCountry,
  setCounty,
  getPatientCountyOptions,
  setState,
  setZipCode,
  setResident,
  setAltHomeResidence,
  setAltHomeResidencePNNV,
  setCitizenship,
} from '../store/patientDemographics.actions'
import PhoneDetails from '../phoneDetails/PhoneDetails.component'
import EmailDetails from '../emailDetails/EmailDetails.component'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import { zipCodeKeys } from '../../../components/numberPad/NumberPad.types'
import { PatientDemographicsConstants } from '../store/patientDemographics.constants'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import Validation from '../../../components/validation/Validation.component'
import { getAllPNNVOptionsSelector, getPatientDemographicsOptionsSelector } from '../../options/selectors'
import { ICity, ICountyOption, IOption, IPNNVOption } from '../../../common/common.types'
import GoogleMap from '../../../components/googleMap/GoogleMap.component'
import SInput from '../../../components/stringInput/StringInput.component'
import { setZipState } from '../../../components/zip/store/zip.actions'
import PopupComponent from '../../../components/infoPopup/Popup.component'
import './AddressPatientInformationSection.component.css'
import { getPickupAddressSelector } from '../../pickupAddress/selectors'
import { getPatientCityNemsisCode } from '../../locationSearch/store/locationSearch.actions'
import { IConfigurationsState } from '../../configurations/store/configurations.types'
import { getConfigurationsSelector } from '../../configurations/selectors'

const AddressPatientInformation: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const patientDemographics = useSelector(getPatientDemographicsSelector)
  const {
    address1,
    address2,
    addressPNNV,
    zipCode,
    city,
    cityPNNV,
    state,
    county,
    resident,
    country,
    altHomeResidence,
    altHomeResidencePNNV,
    citizenship,
  } = patientDemographics
  const patientDemographicsOptions = useSelector(getPatientDemographicsOptionsSelector)
  const { countryOptions, stateOptions, patientCountyOptions, residenceOptions, altHomeResidenceOptions } =
    patientDemographicsOptions
  const pickupAddressState = useSelector(getPickupAddressSelector)
  const configurationsState: IConfigurationsState = useSelector(getConfigurationsSelector)
  const { serviceConfig } = configurationsState
  const { PR_PTCITIZENSHIP, PR_PTRESIDENT } = serviceConfig
  const { isTablet: isTabletDevice } = useDeviceMetrics()
  const [flag, setFlag] = useState(true)
  const [isPatientCountyEnabled, setIsPatientCountyEnabled] = useState(false)
  const [importAddressPopup, setImportAddressPopup] = useState(false)

  const allPNNVOptions = useSelector(getAllPNNVOptionsSelector)
  const addressOptions = allPNNVOptions?.['ePatient.05']
  const cityOptions = allPNNVOptions?.['ePatient.06']

  const setAddress1OnChange = (e?: CustomEvent) => {
    if (e) {
      dispatch(setAddress1(e.detail.value))
      save()
    }
  }

  const setAddressPNNVOnChange = (option: IPNNVOption) => {
    dispatch(setAddressPNNV(option))
    dispatch(setAddress1(''))
    dispatch(setAddress2(''))
    save()
  }

  const setAddress2OnChange = (e?: CustomEvent) => {
    if (e) {
      dispatch(setAddress2(e.detail.value))
      save()
    }
  }

  const setZipCodeOnChange = (numbers: string[]) => {
    dispatch(setZipCode(numbers.join('')))
    if (numbers.length === 5) {
      dispatch(
        setZipState(numbers.join(''), PatientDemographicsConstants.SET_CITY, PatientDemographicsConstants.SET_STATE)
      )
    }
    save()
  }

  const setCityOnChange = (e: CustomEvent) => {
    if (e && 'preventDefault' in e) {
      e.preventDefault()
    }
    const updatedCity: ICity = { name: e.detail.value, nemsisCode: '' }
    if (e.detail.value === city?.name && city?.nemsisCode) {
      updatedCity.nemsisCode = city.nemsisCode
    }
    dispatch(setCity(updatedCity))
    save()
  }

  const setCityPNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setCityPNNV(option))
    dispatch(setCity(''))
    save()
  }

  const setCountyOnChange = (option: ICountyOption) => {
    dispatch(setCounty(option))
    save()
  }

  const setResidentOnChange = (option: IOption) => {
    dispatch(setResident(option))
    save()
  }

  const setAltHomeResidenceOnChange = (option: IOption) => {
    dispatch(setAltHomeResidence(option))
    save()
  }

  const setAltHomeResidencePNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setAltHomeResidencePNNV(option))
    save()
  }

  const setCitizenshipOnChange = (option: IOption) => {
    dispatch(setCitizenship(option))
    save()
  }

  const setCountryOnChange = (option: IOption) => {
    dispatch(setCountry(option))
    save()
  }

  const setStateOnChange = (option: IOption) => {
    if (county?.state !== option?.id) {
      dispatch(setCounty(''))
    }

    dispatch(setState(option))
    save()
  }

  const save = () => {
    dispatch(savePatientDemographics())
  }

  const handleCountyControlBasedOnPatientState = () => {
    const enablePatientCounty = state ? true : false

    if (enablePatientCounty) {
      dispatch(getPatientCountyOptions())
    }

    setIsPatientCountyEnabled(enablePatientCounty)
  }

  useEffect(() => {
    if (flag) {
      address1 && dispatch(setAddress1(address1))
      address2 && dispatch(setAddress2(address2))
      zipCode && dispatch(setZipCode(zipCode))
      if (zipCode && zipCode.length === 5 && !city && !state) {
        dispatch(setZipState(zipCode, PatientDemographicsConstants.SET_CITY, PatientDemographicsConstants.SET_STATE))
      }
      city && dispatch(setCity(city))
      county && dispatch(setCounty(county))
    }
  }, [flag])

  useEffect(() => {
    handleCountyControlBasedOnPatientState()
  }, [state])

  useEffect(() => {
    setFlag(true)

    return () => {
      setFlag(false)
    }
  }, [])

  const checkCopyAddressDataPresent = () => {
    if (
      pickupAddressState?.addressLine1 ||
      pickupAddressState?.addressLine2 ||
      pickupAddressState?.aptSuiteRoom ||
      pickupAddressState?.city ||
      pickupAddressState?.state ||
      pickupAddressState?.zip ||
      pickupAddressState?.county ||
      pickupAddressState?.country
    ) {
      return true
    }
    return false
  }
  const getAddressLine2WithAptSuite = (str1: string, str2: string): string => {
    return !!str1.trim() && !!str2.trim()
      ? pickupAddressState?.addressLine2 + ', ' + pickupAddressState?.aptSuiteRoom
      : pickupAddressState?.addressLine2 + '' + pickupAddressState?.aptSuiteRoom
  }

  const handleImportAddress = () => {
    setImportAddressPopup(false)
    dispatch(setAddress1(pickupAddressState?.addressLine1))
    dispatch(
      setAddress2(getAddressLine2WithAptSuite(pickupAddressState?.addressLine2, pickupAddressState?.aptSuiteRoom))
    )
    dispatch(setCity(pickupAddressState?.city))
    dispatch(setState(pickupAddressState?.state))
    dispatch(setZipCode(pickupAddressState?.zip))
    dispatch(setCounty(pickupAddressState?.county))
    dispatch(setCountry(pickupAddressState?.country))
    save()
  }

  const importPopupContent = () => {
    return (
      <>
        <h1>Address Copy</h1>
        <hr className='field-divider' />
        <IonGrid className='ion-no-margin p-1'>
          <h6>Use this address as patient address?</h6>
          <IonRow className='mt-1'>
            <IonCol>{t('patientDemographics.addressInfo.Address1')}</IonCol>
            <IonCol>{pickupAddressState?.addressLine1} </IonCol>
          </IonRow>
          <hr />
          <IonRow>
            <IonCol>{t('patientDemographics.addressInfo.Address2')}</IonCol>
            <IonCol>
              {getAddressLine2WithAptSuite(pickupAddressState?.addressLine2, pickupAddressState?.aptSuiteRoom)}
            </IonCol>
          </IonRow>
          <hr />
          <IonRow>
            <IonCol>{t('patientDemographics.addressInfo.City')}</IonCol>
            <IonCol>{pickupAddressState?.city?.name} </IonCol>
          </IonRow>
          <hr />
          <IonRow>
            <IonCol>{t('patientDemographics.addressInfo.State')}</IonCol>
            <IonCol>{pickupAddressState?.state?.name} </IonCol>
          </IonRow>
          <hr />
          <IonRow>
            <IonCol>{t('patientDemographics.addressInfo.ZipCode')}</IonCol>
            <IonCol>{pickupAddressState?.zip} </IonCol>
          </IonRow>
          <hr />
          <IonRow>
            <IonCol>{t('patientDemographics.addressInfo.County')}</IonCol>
            <IonCol>{pickupAddressState?.county?.name} </IonCol>
          </IonRow>
          <hr />
          <IonRow>
            <IonCol>{t('patientDemographics.addressInfo.Country')}</IonCol>
            <IonCol>{pickupAddressState?.country?.name} </IonCol>
          </IonRow>
          <hr />
        </IonGrid>
      </>
    )
  }

  return (
    <div id='item-list'>
      <IonItemDivider color='light' className='ion-padding'>
        <IonLabel className='grid-item-divider-label' color='medium'>
          {t('patientDemographics.addressInfo.PatientAddressAndContactInformation')}
        </IonLabel>

        {pickupAddressState.typeValue?.id === 0 && checkCopyAddressDataPresent() && (
          <IonRow className='w-100 d-flex justify-content-end '>
            <IonButton
              data-testid='ImportAddress'
              className=' transform-none'
              fill='clear'
              color='primary-alt'
              onClick={(e) => {
                setImportAddressPopup(true)
              }}>
              <IonLabel className='m-5'>{t('patientDemographics.addressInfo.CopyAddress')}</IonLabel>
            </IonButton>

            <PopupComponent
              id='popup-container'
              dataTestId='importPopupModal'
              isOpen={importAddressPopup}
              handleHidePopover={() => setImportAddressPopup(false)}
              className='import-address-container'>
              {importPopupContent()}
              <IonButtons className='ion-margin'>
                <IonButton
                  className='btn btn-secondary btn-standard mr-8'
                  expand='block'
                  onClick={(e) => setImportAddressPopup(false)}>
                  {t('common.buttons.No')}
                </IonButton>
                <IonButton className='btn btn-standard btn-primary' expand='block' onClick={handleImportAddress}>
                  {t('common.buttons.Yes')}
                </IonButton>
              </IonButtons>
            </PopupComponent>
          </IonRow>
        )}
      </IonItemDivider>
      <IonList className={isTabletDevice ? 'ion-margin-horizontal' : ''}>
        <IonGrid className='no-padding'>
          <IonRow>
            <IonCol className='picklist'>
              <SInput
                isPHI
                label={t('patientDemographics.addressInfo.Address1')}
                dataTestID='Address1'
                inputValue={address1}
                onChangeAction={setAddress1OnChange}
                pnnvOptions={addressOptions}
                pnnvValue={addressPNNV}
                pnnvAction={setAddressPNNVOnChange}
              />
              <Validation id={PatientDemographicsConstants.SET_ADDRESS_1} />
            </IonCol>
            <IonCol size='auto' className='mapAlign'>
              {((address1 && zipCode) || (address1 && city && state)) && (
                <GoogleMap
                  address={address1}
                  city={city?.name}
                  state={state?.name}
                  zip={zipCode || undefined}
                  country={country?.name}
                />
              )}
            </IonCol>
          </IonRow>

          <hr className='field-divider' />

          <IonRow>
            <IonCol className='picklist'>
              <SInput
                isPHI
                label={t('patientDemographics.addressInfo.Address2')}
                dataTestID='Address2'
                inputValue={address2}
                onChangeAction={setAddress2OnChange}
                pnnvOptions={addressOptions}
                pnnvValue={addressPNNV}
                pnnvAction={setAddressPNNVOnChange}
              />
              <Validation id={PatientDemographicsConstants.SET_ADDRESS_2} />
            </IonCol>
          </IonRow>

          <hr className='field-divider' />
        </IonGrid>
      </IonList>

      <IonItemDivider color='light' />

      <IonList className={isTabletDevice ? 'ion-margin-horizontal' : ''}>
        <IonGrid className='no-padding'>
          <IonRow>
            <IonCol className='picklist'>
              <NumberPad
                isPHI
                elementId='ZipCode'
                dataTestId='ZipCode'
                values={zipCode ? zipCode.toString().split('') : []}
                label={t('patientDemographics.addressInfo.ZipCode')}
                onNumPadClick={setZipCodeOnChange}
                validCharacters={zipCodeKeys}
              />
              <Validation id={PatientDemographicsConstants.SET_ZIP_CODE} />
            </IonCol>
          </IonRow>

          <hr className='field-divider' />

          <IonRow>
            <IonCol className='picklist'>
              <SInput
                isPHI
                label={t('patientDemographics.addressInfo.City')}
                dataTestID='City'
                inputValue={city?.name || ''}
                onChangeAction={setCityOnChange}
                onBlurAction={() => dispatch(getPatientCityNemsisCode())}
                pnnvOptions={cityOptions}
                pnnvValue={cityPNNV}
                pnnvAction={setCityPNNVOnChange}
              />
              <Validation id={PatientDemographicsConstants.SET_CITY} />
            </IonCol>

            <IonCol className='picklist'>
              <SingleSelect
                dataTestID='State'
                selectedOption={state}
                listHeader={t('patientDemographics.addressInfo.State')}
                label={t('patientDemographics.addressInfo.State')}
                options={stateOptions}
                action={setStateOnChange}
              />
              <Validation id={PatientDemographicsConstants.SET_STATE} />
            </IonCol>
          </IonRow>

          <hr className='field-divider' />

          <IonRow>
            <IonCol className='picklist'>
              <SingleSelect
                isPHI
                listHeader={t('patientDemographics.addressInfo.County')}
                label={t('patientDemographics.addressInfo.County')}
                dataTestID='County'
                options={patientCountyOptions}
                selectedOption={county}
                action={setCountyOnChange}
                disabled={!isPatientCountyEnabled}
              />
              <Validation id={PatientDemographicsConstants.SET_COUNTY} />
            </IonCol>
          </IonRow>

          <hr className='field-divider' />

          <IonRow>
            {(PR_PTRESIDENT === 1 || resident) && (
              <IonCol className='picklist'>
                <SingleSelect
                  isPHI
                  listHeader={t('patientDemographics.addressInfo.Resident')}
                  label={t('patientDemographics.addressInfo.Resident')}
                  dataTestID='Resident'
                  options={residenceOptions}
                  selectedOption={resident}
                  action={setResidentOnChange}
                />
                <Validation id={PatientDemographicsConstants.SET_RESIDENT} />
              </IonCol>
            )}
            <IonCol className='picklist'>
              <SingleSelect
                dataTestID='AltHomeResidence'
                selectedOption={altHomeResidence}
                listHeader={t('patientDemographics.addressInfo.AltHomeResidence')}
                options={altHomeResidenceOptions}
                label={t('patientDemographics.addressInfo.AltHomeResidence')}
                action={setAltHomeResidenceOnChange}
                pnnvAction={setAltHomeResidencePNNVOnChange}
                selectedPNNVOption={altHomeResidencePNNV}
              />

              <Validation id={PatientDemographicsConstants.SET_ALT_HOME_RESIDENCE} />
            </IonCol>
          </IonRow>

          <hr className='field-divider' />

          <IonRow>
            <IonCol className='picklist'>
              <SingleSelect
                dataTestID='Country'
                selectedOption={country}
                label={t('patientDemographics.addressInfo.Country')}
                listHeader={t('patientDemographics.addressInfo.Country')}
                options={countryOptions}
                action={setCountryOnChange}
              />
            </IonCol>
          </IonRow>
          <hr className='field-divider' />

          {(PR_PTCITIZENSHIP === 1 || citizenship) && (
            <>
              <IonRow>
                <IonCol className='picklist'>
                  <SingleSelect
                    dataTestID='Citizenship'
                    selectedOption={citizenship}
                    label={t('patientDemographics.addressInfo.Citizenship')}
                    listHeader={t('patientDemographics.addressInfo.Citizenship')}
                    options={countryOptions}
                    action={setCitizenshipOnChange}
                  />
                </IonCol>
              </IonRow>
              <hr className='field-divider' />
            </>
          )}
        </IonGrid>
      </IonList>

      <IonItemDivider color='light' />

      <PhoneDetails />

      <IonItemDivider color='light' />

      <EmailDetails isPHI />

      <IonItemDivider color='light' />
    </div>
  )
}

export default AddressPatientInformation
